import React, { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { StyleSheet, View, Pressable, ScrollView, Platform, Alert } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { onboardingColors } from '@/components/Onboarding/shared/NativeOnboardingChrome';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import {
  PRICING_PLANS,
  PLUS_MONTHLY_CREDITS_DISPLAY,
  PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
} from '@/constants/config';
import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { accountApi } from '@/lib/api';
import { useAuthStore, useToastStore } from '@/store';
import { openBtcPayCheckout, btcPayCheckoutOpenError } from '@/lib/btcpayCheckout';
import { nativeSubscriptionPlans } from '@/lib/nativePayment';
import InternationalPaymentNotice from '@/components/InternationalPaymentNotice';
import { trackEvent } from '@/constants/utils/analytics';

// Matches the Plus CTA button gradient so accents read as one brand system.
const BRAND_GRADIENT = ['#0D5C47', '#0D5C47'];
const WORK_PLUS_BENEFITS = [
  { icon: 'briefcase', boldText: 'From idea to finished project', text: 'Research, editable files, websites and games.', color: '#1F6B47' },
  { icon: 'infinity', boldText: 'Everyday chats with Arya included', text: 'Write, plan and brainstorm without using credits.', color: '#5FB68F' },
  { icon: 'sparkles', boldText: '100+ models. One place to create.', text: 'Images, video, voice, code and your own agents.', color: '#7C3AED' },
];

const createStyles = (colors, isXS, withPadding, isDark, withScroll) => StyleSheet.create({
  header: {
    width: '100%',
    paddingTop: withPadding ? (isXS ? 12 : 12) : 0,
    paddingHorizontal: withPadding ? (isXS ? 18 : 20) : 0,
  },
  headerUpper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isXS ? 10 : 10,
    marginBottom: isXS ? 6 : 5,
  },
  headerIconBadge: {
    width: isXS ? 28 : 30,
    height: isXS ? 28 : 30,
    borderRadius: isXS ? 8 : 9,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  outerContainer: {
    width: '100%',
    ...(withScroll ? { flex: 1 } : null),
  },
  innerContainer: {
    width: '100%',
    paddingHorizontal: withPadding ? (isXS ? 18 : 20) : 0,
    paddingTop: withPadding ? (isXS ? 8 : 6) : 0,
    ...(withScroll ? { flex: 1 } : null),
  },
  scrollContent: {
    paddingBottom: withPadding ? (isXS ? 12 : 10) : 0,
    gap: isXS ? 12 : 10,
    ...(withScroll ? { flexGrow: 1 } : null),
  },
  title: {
    textAlign: 'left',
    flex: 1,
    minWidth: 0,
  },
  subtitle: {
    textAlign: 'left',
    marginBottom: isXS ? 8 : 2,
  },
  // Desktop / tablet: stacked feature rows. Mobile: wrapping chips.
  features: {
    gap: isXS ? 7 : 6,
    marginBottom: isXS ? 2 : 2,
    ...(isXS ? {
      flexDirection: 'row',
      flexWrap: 'wrap',
    } : null),
  },
  feature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isXS ? 6 : 8,
    ...(isXS ? {
      paddingVertical: 5,
      paddingHorizontal: 9,
      borderRadius: 999,
      backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
    } : null),
  },
  featureIcon: {
    width: isXS ? 20 : 25,
    height: isXS ? 20 : 25,
    borderRadius: isXS ? 6 : 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 1px 2px rgba(0,0,0,0.18)',
    } : {
      shadowColor: '#000',
      shadowOpacity: 0.18,
      shadowRadius: 2,
      shadowOffset: { width: 0, height: 1 },
      elevation: 2,
    }),
  },
  featureTextContainer: {
    flex: isXS ? undefined : 1,
  },
  cadenceToggle: {
    flexDirection: 'row',
    borderRadius: isXS ? 10 : 12,
    padding: 3,
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    gap: 3,
  },
  cadenceOption: {
    flex: 1,
    paddingVertical: isXS ? 10 : 9,
    paddingHorizontal: 8,
    borderRadius: isXS ? 8 : 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
  },
  cadenceOptionSelected: {
    backgroundColor: colors.solid_color_primary,
    ...(Platform.OS === 'web' ? {
      boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.12)',
    } : {
      shadowColor: '#000',
      shadowOpacity: 0.1,
      shadowRadius: 3,
      shadowOffset: { width: 0, height: 1 },
      elevation: 2,
    }),
  },
  saveChip: {
    backgroundColor: 'rgba(34, 197, 94, 0.18)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  saveChipText: {
    color: 'rgb(34, 197, 94)',
    fontSize: 10,
  },
  planCard: {
    borderRadius: isXS ? 12 : 14,
    borderWidth: 1.5,
    borderColor: isDark ? 'rgba(255,255,255,0.14)' : colors.border_color_secondary,
    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : colors.solid_color_primary,
    paddingVertical: isXS ? 14 : 12,
    paddingHorizontal: isXS ? 14 : 12,
    gap: isXS ? 8 : 8,
    position: 'relative',
    overflow: 'hidden',
  },
  planCardSelected: {
    borderColor: isDark ? '#178A68' : colors.accent_color_primary,
    borderWidth: 2,
    ...(Platform.OS === 'web' ? {
      boxShadow: isDark
        ? '0 0 0 1px rgba(13, 92, 71, 0.40), 0 6px 28px rgba(13, 92, 71, 0.28)'
        : '0 0 0 1px rgba(13, 92, 71, 0.22), 0 6px 20px rgba(13, 92, 71, 0.14)',
    } : {
      shadowColor: colors.accent_color_primary,
      shadowOpacity: isDark ? 0.45 : 0.2,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 3 },
      elevation: 4,
    }),
  },
  planCardWash: {
    opacity: isDark ? 0.28 : 0.12,
  },
  planCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: isXS ? 8 : 10,
  },
  radio: {
    width: isXS ? 18 : 20,
    height: isXS ? 18 : 20,
    borderRadius: isXS ? 9 : 10,
    borderWidth: 2,
    // Unselected state must stay legible on both card backgrounds — the theme
    // border token is far too faint here.
    borderColor: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(17, 24, 39, 0.35)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(17, 24, 39, 0.04)',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    flexShrink: 0,
  },
  radioSelected: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    overflow: 'hidden',
  },
  planCardBody: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  planNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  popularBadge: {
    paddingHorizontal: isXS ? 6 : 8,
    paddingVertical: isXS ? 2 : 3,
    borderRadius: 999,
    overflow: 'hidden',
  },
  popularBadgeText: {
    color: '#FFFFFF',
    fontSize: isXS ? 9 : 10,
    letterSpacing: 0.4,
  },
  planMeta: {
    color: colors.text_color_secondary,
  },
  priceBlock: {
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 5,
  },
  priceStrike: {
    textDecorationLine: 'line-through',
    opacity: 0.7,
  },
  pricePeriod: {
    marginLeft: 2,
  },
  billedNote: {
    marginTop: 1,
  },
  premiumPerks: {
    gap: isXS ? 4 : 5,
    paddingLeft: isXS ? 26 : 30,
    paddingTop: isXS ? 2 : 0,
    ...(isXS ? {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: 10,
      rowGap: 6,
    } : null),
  },
  premiumPerkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: isXS ? 4 : 8,
  },
  premiumPerkIcon: {
    width: isXS ? 14 : 17,
    height: isXS ? 14 : 17,
    borderRadius: isXS ? 4 : 6,
    alignItems: 'center',
    justifyContent: 'center',
    // Solid brand-purple chip — fixed hues because some theme accents (e.g.
    // soft purple #D4A5E8) can't carry a white glyph. The tinted variant was
    // unreadable on the selected card's gradient wash in both themes.
    backgroundColor: isDark ? '#A855F7' : '#7C3AED',
    flexShrink: 0,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginVertical: isXS ? 2 : 0,
  },
  dividerLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors.border_color_secondary,
  },
  bitcoinLink: {
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: isXS ? 8 : 6,
    paddingHorizontal: 12,
    borderRadius: 999,
  },
  bitcoinIcon: {
    width: isXS ? 16 : 18,
    height: isXS ? 16 : 18,
    borderRadius: isXS ? 8 : 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F7931A',
  },
  bitcoinLinkText: {
    textDecorationLine: 'underline',
  },
  bitcoinChooserHint: {
    textAlign: 'center',
    marginBottom: 2,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: isXS ? 8 : 0,
    paddingTop: 0,
  },
  stars: {
    flexDirection: 'row',
    gap: 2,
  },
});

function resolveSelectedPlan(tier, cadence, payWithBitcoin) {
  if (payWithBitcoin) {
    return tier === 'premium' ? 'bitcoin-premium-annual' : 'bitcoin-plus-annual';
  }
  if (tier === 'premium') {
    return cadence === 'annual' ? 'premium-annual' : 'premium-monthly';
  }
  return cadence === 'annual' ? 'annual' : 'monthly';
}

function Radio({ selected, styles, gradient = BRAND_GRADIENT }) {
  return (
    <View style={[styles.radio, selected && styles.radioSelected]}>
      {selected ? (
        <>
          <LinearGradient
            colors={gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <Icon name="check" size={10} solid color="#FFFFFF" />
        </>
      ) : null}
    </View>
  );
}

function GradientWash({ styles, gradient = BRAND_GRADIENT, onboarding }) {
  if (onboarding) return <View pointerEvents="none" style={[StyleSheet.absoluteFill, { backgroundColor: onboardingColors.selected }]} />;
  return (
    <LinearGradient
      colors={gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[StyleSheet.absoluteFill, styles.planCardWash]}
      pointerEvents="none"
    />
  );
}

const PricingUpgrade = forwardRef(({
  title = 'Unlock Work mode with Gab AI Plus',
  subtitle = 'Give bigger jobs to AI agents that can plan, research, use tools, and deliver finished work.',
  withPadding = true,
  withScroll = true,
  promoCode,
  onSelectionChange,
  defaultTier = 'plus',
  initialCadence = 'annual',
  hidePlusCard = false,
  onDismiss,
  onboarding = false,
}, ref) => {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const gradient = onboarding ? [onboardingColors.ink, onboardingColors.ink] : BRAND_GRADIENT;
  const [tier, setTier] = useState((Platform.OS === 'web' || Platform.OS === 'macos') ? defaultTier : 'plus');
  const [cadence, setCadence] = useState(initialCadence === 'monthly' ? 'monthly' : 'annual');
  const [payWithBitcoin, setPayWithBitcoin] = useState(false);
  const { isXS } = useResponsiveness();
  const { openCheckout, iap } = useGabPurchase();
  const isNative = (Platform.OS !== 'web' && Platform.OS !== 'macos');
  const nativePlans = nativeSubscriptionPlans(iap?.products, Platform.OS);
  const nativePlan = nativePlans.find(plan => plan.cadence === (cadence === 'annual' ? 'yearly' : 'weekly'));
  const [isLoading, setIsLoading] = useState(false);

  const styles = useMemo(
    () => {
      const base = createStyles(colors, isXS, withPadding, isDark, withScroll);
      if (!onboarding) return base;
      return {
        ...base,
        planCard: { ...base.planCard, borderRadius: 16, borderColor: onboardingColors.border },
        planCardSelected: { borderColor: onboardingColors.ink, borderWidth: 2 },
        saveChip: { ...base.saveChip, backgroundColor: onboardingColors.selected },
        saveChipText: { ...base.saveChipText, color: onboardingColors.ink },
        premiumPerkIcon: { ...base.premiumPerkIcon, backgroundColor: onboardingColors.ink },
      };
    },
    [colors, isXS, withPadding, isDark, withScroll, onboarding],
  );

  const selectedPlan = isNative ? (cadence === 'annual' ? 'annual' : 'weekly') : resolveSelectedPlan(tier, cadence, payWithBitcoin);
  const showPremium = (Platform.OS === 'web' || Platform.OS === 'macos');
  const showBitcoin = (Platform.OS === 'web' || Platform.OS === 'macos');
  const ctaLabel = payWithBitcoin ? 'Pay with Bitcoin' : `Continue with ${tier === 'premium' ? 'Plus Premium' : 'Plus'}`;

  useEffect(() => {
    onSelectionChange?.({ tier, cadence, selectedPlan, ctaLabel, payWithBitcoin });
  }, [tier, cadence, selectedPlan, ctaLabel, payWithBitcoin, onSelectionChange]);

  const plusPrice = payWithBitcoin
    ? PRICING_PLANS.plus.bitcoinAnnualPrice
    : cadence === 'annual'
      ? PRICING_PLANS.plus.annualMonthlyPrice
      : PRICING_PLANS.plus.monthlyPrice;
  const premiumPrice = payWithBitcoin
    ? PRICING_PLANS.plusPremium.bitcoinAnnualPrice
    : cadence === 'annual'
      ? PRICING_PLANS.plusPremium.annualMonthlyPrice
      : PRICING_PLANS.plusPremium.monthlyPrice;
  const plusBilledNote = payWithBitcoin
    ? 'Billed once yearly in Bitcoin'
    : cadence === 'annual'
      ? `$${PRICING_PLANS.plus.annualPrice}/yr`
      : 'Billed monthly';
  const premiumBilledNote = payWithBitcoin
    ? 'Billed once yearly in Bitcoin'
    : cadence === 'annual'
      ? `$${PRICING_PLANS.plusPremium.annualPrice}/yr`
      : 'Billed monthly';

  // Anchor price: what the same plan costs without the annual/Bitcoin discount.
  // Bitcoin quotes a yearly total, so it compares against the card annual price.
  const plusAnchorPrice = payWithBitcoin
    ? PRICING_PLANS.plus.annualPrice
    : cadence === 'annual'
      ? PRICING_PLANS.plus.monthlyPrice
      : null;
  const premiumAnchorPrice = payWithBitcoin
    ? PRICING_PLANS.plusPremium.annualPrice
    : cadence === 'annual'
      ? PRICING_PLANS.plusPremium.monthlyPrice
      : null;

  const selectTier = (nextTier) => {
    setTier(nextTier);
    trackEvent('conversion', 'selected_plan', resolveSelectedPlan(nextTier, cadence, payWithBitcoin));
  };

  const selectCadence = (nextCadence) => {
    setCadence(nextCadence);
    trackEvent('conversion', 'selected_plan', resolveSelectedPlan(tier, nextCadence, false));
  };

  const enterBitcoinMode = () => {
    setPayWithBitcoin(true);
    if (hidePlusCard) setTier('premium');
    else if (tier !== 'plus' && tier !== 'premium') setTier('plus');
    trackEvent('conversion', 'selected_plan', resolveSelectedPlan(hidePlusCard ? 'premium' : tier, 'annual', true));
  };

  const exitBitcoinMode = () => {
    setPayWithBitcoin(false);
    trackEvent('conversion', 'selected_plan', resolveSelectedPlan(tier, cadence, false));
  };

  const handleBitcoinUpgrade = async () => {
    const { addToast } = useToastStore.getState().actions;
    const expectedUserId = Platform.OS === 'macos' ? (useAuthStore.getState().user?.id ?? null) : undefined;
    setIsLoading(true);
    const plan = tier === 'premium' ? 'plus-premium-annual' : 'plus-annual-200';
    try {
      const response = await accountApi.createBtcPayInvoice({ plan });
      const checkoutUrl = response?.checkoutUrl || response?.data?.checkoutUrl;

      if (checkoutUrl) {
        const invoiceId = response?.invoiceId || response?.data?.invoiceId;
        await openBtcPayCheckout({
          checkoutUrl,
          invoiceId,
          expectedUserId,
          kind: 'plus',
          onSettled: () => {
            if (Platform.OS !== 'macos') {
              useAuthStore.getState().fetchUser?.();
              useAuthStore.getState().fetchUserCredits?.({ force: true });
            }
            addToast(
              tier === 'premium' ? 'Plus Premium is now active.' : 'Plus is now active.',
              'success',
            );
          },
        });
        addToast(
          tier === 'premium'
            ? 'Bitcoin payment opened. Complete payment to activate Plus Premium.'
            : 'Bitcoin payment opened. Complete payment to activate Plus.',
          'info',
        );
        return true;
      }

      const openError = btcPayCheckoutOpenError(response);
      addToast(openError.message, openError.type);
      return false;
    } catch (error) {
      console.error('Bitcoin checkout error:', error);
      addToast(error?.message || 'Failed to create Bitcoin payment', 'error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (isTrial = false) => {
    setIsLoading(true);
    trackEvent('conversion', 'checkout_started', selectedPlan);
    try {
      if (isNative) {
        if (hidePlusCard) throw new Error('Plus Premium is managed on the web.');
        if (!nativePlan) throw new Error('This store plan is unavailable. Please try again later.');
        const result = await openCheckout({ plusPlanId: nativePlan.cadence });
        if (result?.pending || result?.pendingValidation) {
          useToastStore.getState().actions.addToast('Your purchase is pending verification. Use Restore; do not purchase again.', 'info');
          return false;
        }
        if (result?.cancelled) return false;
        if (!result?.success) throw new Error(result?.error || 'Unable to complete purchase.');
        return true;
      }
      if (selectedPlan === 'monthly') {
        await openCheckout({ plusPlanId: 'monthly', isTrial, promoCode: promoCode || undefined });
      } else if (selectedPlan === 'annual') {
        await openCheckout({ plusPlanId: 'yearly', isTrial, promoCode: promoCode || undefined });
      } else if (selectedPlan === 'premium-monthly' && (Platform.OS === 'web' || Platform.OS === 'macos')) {
        await openCheckout({ plusPlanId: 'premium-monthly', isTrial, promoCode: promoCode || undefined });
      } else if (selectedPlan === 'premium-annual' && (Platform.OS === 'web' || Platform.OS === 'macos')) {
        await openCheckout({ plusPlanId: 'premium-yearly', isTrial, promoCode: promoCode || undefined });
      } else if (
        (selectedPlan === 'bitcoin-plus-annual' || selectedPlan === 'bitcoin-premium-annual')
        && Platform.OS !== 'ios'
      ) {
        return await handleBitcoinUpgrade();
      }
      return true;
    } catch (error) {
      console.error('Subscription error:', error);
      if (Platform.OS !== 'web') {
        Alert.alert('Purchase Error', error?.message || 'Unable to start subscription purchase.');
      } else {
        useToastStore.getState().actions.addToast(error?.message || 'Unable to start subscription purchase.', 'error');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleUpgrade,
    isLoading,
    isAnnual: payWithBitcoin || cadence === 'annual',
    selectedPlan,
    ctaLabel,
  }));

  const plusSelected = tier === 'plus';
  const premiumSelected = tier === 'premium';

  return (
    <>
      <View style={styles.header}>
        <View style={styles.headerUpper}>
          <View style={styles.headerIconBadge}>
            <LinearGradient
              colors={gradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={StyleSheet.absoluteFill}
            />
            <Icon name="sparkles" size={isXS ? 14 : 15} solid color="#FFFFFF" />
          </View>
          <Text size={isXS ? 'l' : 'xxl'} weight="bold" color="primary" style={styles.title}>
            {title}
          </Text>
          {onDismiss && (
            <Pressable onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Close plan options" hitSlop={8} style={{ padding: 6 }}>
              <Icon name="xmark" size={16} color={colors.text_color_secondary} />
            </Pressable>
          )}
        </View>
        <Text
          size={isXS ? 's' : 'm'}
          color="secondary"
          style={styles.subtitle}
          numberOfLines={isXS ? 4 : undefined}
        >
          {subtitle}
        </Text>
      </View>

      <View style={styles.outerContainer}>
        {(() => {
          const body = (
            <>
              {!hidePlusCard ? (
                <View style={styles.features}>
                  {WORK_PLUS_BENEFITS.map((f) => (
                    <View key={f.icon} style={styles.feature}>
                      <View style={[styles.featureIcon, { backgroundColor: onboarding ? onboardingColors.ink : f.color }]}>
                        <Icon name={f.icon} size={isXS ? 10 : 12} solid color="#FFFFFF" />
                      </View>
                      {isXS ? (
                        <Text size="xs" weight="bold" color="primary">
                          {f.boldText}
                        </Text>
                      ) : (
                        <View style={styles.featureTextContainer}>
                          <Text size="s" weight="semibold">{f.boldText}</Text>
                          <Text size="xs" color="secondary" style={{ lineHeight: 17 }}>{f.text}</Text>
                        </View>
                      )}
                    </View>
                  ))}
                </View>
              ) : null}

              {payWithBitcoin ? (
                <Text size="s" color="secondary" style={styles.bitcoinChooserHint}>
                  Bitcoin is available for annual plans only. Choose a plan:
                </Text>
              ) : (
                <View style={styles.cadenceToggle}>
                  <Pressable
                    style={[styles.cadenceOption, cadence === 'annual' && styles.cadenceOptionSelected]}
                    onPress={() => selectCadence('annual')}
                    accessibilityRole="radio"
                    accessibilityLabel="Annual billing"
                    accessibilityState={{ checked: cadence === 'annual' }}
                  >
                    <Text weight={cadence === 'annual' ? 'bold' : 'medium'} size="s" color="primary">
                      Annual
                    </Text>
                    {!isNative && <View style={styles.saveChip}>
                      <Text style={styles.saveChipText} weight="bold">
                        {tier === 'premium' ? 'Save 16%' : 'Save 17%'}
                      </Text>
                    </View>}
                  </Pressable>
                  <Pressable
                    style={[styles.cadenceOption, cadence === 'monthly' && styles.cadenceOptionSelected]}
                    onPress={() => selectCadence('monthly')}
                    accessibilityRole="radio"
                    accessibilityLabel={isNative ? 'Weekly billing' : 'Monthly billing'}
                    accessibilityState={{ checked: cadence === 'monthly' }}
                  >
                    <Text weight={cadence === 'monthly' ? 'bold' : 'medium'} size="s" color="primary">
                      {isNative ? 'Weekly' : 'Monthly'}
                    </Text>
                  </Pressable>
                </View>
              )}

              {/* Plus — primary plan */}
              {!hidePlusCard ? (
                <Pressable
                  onPress={() => selectTier('plus')}
                  accessibilityRole="radio"
                  accessibilityLabel="Gab AI Plus"
                  accessibilityState={{ checked: plusSelected }}
                  style={[
                    styles.planCard,
                    plusSelected && styles.planCardSelected,
                  ]}
                >
                  {plusSelected ? <GradientWash styles={styles} gradient={gradient} onboarding={onboarding} /> : null}
                  <View style={styles.planCardHeader}>
                    <Radio selected={plusSelected} styles={styles} gradient={gradient} />
                    <View style={styles.planCardBody}>
                      <View style={styles.planNameRow}>
                        <Text size={isXS ? 'n' : 'l'} weight="bold" color="primary">Plus</Text>
                        {!payWithBitcoin ? (
                          <View style={styles.popularBadge}>
                            <LinearGradient
                              colors={gradient}
                              start={{ x: 0, y: 0 }}
                              end={{ x: 1, y: 0 }}
                              style={StyleSheet.absoluteFill}
                            />
                            <Text weight="bold" style={styles.popularBadgeText}>MOST POPULAR</Text>
                          </View>
                        ) : null}
                      </View>
                      {isXS ? (
                        <>
                          <Text size="xs" style={styles.planMeta}>
                            {PLUS_MONTHLY_CREDITS_DISPLAY} credits/mo
                          </Text>
                          <Text size="xs" style={styles.planMeta}>
                            For everyday projects
                          </Text>
                        </>
                      ) : (
                        <Text size="xs" style={styles.planMeta}>
                          {PLUS_MONTHLY_CREDITS_DISPLAY} credits/mo · up to 3 Work sessions at once
                        </Text>
                      )}
                    </View>
                    <View style={styles.priceBlock}>
                      <View style={styles.priceRow}>
                        {!isNative && plusAnchorPrice ? (
                          <Text size="xs" color="secondary" style={styles.priceStrike}>
                            ${plusAnchorPrice}
                          </Text>
                        ) : null}
                        <Text color="primary" weight="bold" size={isXS ? 'xl' : 'xxl'}>{isNative ? nativePlan?.price || 'Unavailable' : `$${plusPrice}`}</Text>
                        <Text style={styles.pricePeriod} size="xs" color="secondary">
                          {isNative ? nativePlan?.period || '' : payWithBitcoin ? '/yr' : '/mo'}
                        </Text>
                      </View>
                      <Text size="xs" color="secondary" style={styles.billedNote}>{isNative ? 'Renews automatically; store terms apply.' : plusBilledNote}</Text>
                    </View>
                  </View>
                </Pressable>
              ) : null}

              {showPremium ? (
                <>
                  {!hidePlusCard ? (
                    <View style={styles.dividerRow}>
                      <View style={styles.dividerLine} />
                      <Text size="xs" color="secondary">Need more for work?</Text>
                      <View style={styles.dividerLine} />
                    </View>
                  ) : null}

                  <Pressable
                    onPress={() => selectTier('premium')}
                    accessibilityRole="radio"
                    accessibilityLabel="Gab AI Plus Premium"
                    accessibilityState={{ checked: premiumSelected }}
                    style={[
                      styles.planCard,
                      premiumSelected && styles.planCardSelected,
                    ]}
                  >
                    {premiumSelected ? <GradientWash styles={styles} gradient={gradient} onboarding={onboarding} /> : null}
                    <View style={styles.planCardHeader}>
                      <Radio selected={premiumSelected} styles={styles} gradient={gradient} />
                      <View style={styles.planCardBody}>
                        <View style={styles.planNameRow}>
                          <Text size={isXS ? 'n' : 'l'} weight="bold" color="primary">Plus Premium</Text>
                        </View>
                        {!isXS ? (
                          <Text size="s" style={styles.planMeta}>
                            For bigger projects and daily Work
                          </Text>
                        ) : null}
                      </View>
                      <View style={styles.priceBlock}>
                        <View style={styles.priceRow}>
                          {premiumAnchorPrice ? (
                            <Text size="xs" color="secondary" style={styles.priceStrike}>
                              ${premiumAnchorPrice}
                            </Text>
                          ) : null}
                          <Text color="primary" weight="bold" size={isXS ? 'xl' : 'xxl'}>${premiumPrice}</Text>
                          <Text style={styles.pricePeriod} size="xs" color="secondary">
                            {isNative ? nativePlan?.period || '' : payWithBitcoin ? '/yr' : '/mo'}
                          </Text>
                        </View>
                        <Text size="xs" color="secondary" style={styles.billedNote}>{premiumBilledNote}</Text>
                      </View>
                    </View>

                    <View style={styles.premiumPerks}>
                      {[
                        { icon: 'coins', label: `${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits/mo` },
                        { icon: 'briefcase', label: 'Up to 10 Work sessions at once' },
                        { icon: 'gauge-high', label: 'Higher API rate limits' },
                        { icon: 'headset', label: 'Priority email support' },
                      ].map((perk) => (
                        <View key={perk.icon} style={styles.premiumPerkRow}>
                          <View style={styles.premiumPerkIcon}>
                            <Icon name={perk.icon} size={isXS ? 9 : 9} solid color="#FFFFFF" />
                          </View>
                          <Text size="xs" color="secondary">{perk.label}</Text>
                        </View>
                      ))}
                    </View>
                  </Pressable>
                </>
              ) : null}

              {showBitcoin ? (
                payWithBitcoin ? (
                  <Pressable onPress={exitBitcoinMode} style={styles.bitcoinLink}>
                    <Text size="s" color="secondary" style={styles.bitcoinLinkText}>
                      Prefer to pay with card? Go back
                    </Text>
                  </Pressable>
                ) : (
                  <Pressable onPress={enterBitcoinMode} style={styles.bitcoinLink}>
                    <View style={styles.bitcoinIcon}>
                      <Icon name="bitcoin-sign" size={10} solid color="#FFFFFF" />
                    </View>
                    <Text size="s" color="secondary">
                      Want to pay with Bitcoin?{' '}
                      <Text size="s" color="primary" weight="bold" style={styles.bitcoinLinkText}>
                        Click here
                      </Text>
                    </Text>
                  </Pressable>
                )
              ) : null}

              <InternationalPaymentNotice style={isXS ? { paddingVertical: 6, paddingHorizontal: 10 } : undefined} />

              <Text size="xs" color="secondary" style={{ textAlign: 'center', lineHeight: 17 }}>
                Work, media and API use credits. Bigger tasks use more. Add credits anytime. Cancel your plan anytime.
              </Text>
            </>
          );

          if (!withScroll) {
            return (
              <View style={[styles.innerContainer, styles.scrollContent]}>
                {body}
              </View>
            );
          }

          return (
            <ScrollView
              style={styles.innerContainer}
              contentContainerStyle={styles.scrollContent}
            >
              {body}
            </ScrollView>
          );
        })()}
      </View>
    </>
  );
});

export default React.memo(PricingUpgrade);
